/* eslint-disable @typescript-eslint/no-explicit-any */
import { WorkoutExercise, WorkoutTiny } from 'generated/graphql';
import { LegacyWorkoutFormValues } from './workoutEdit.model';
import uuid4 from 'uuid4';
import { omit } from 'lodash';
import { Exercise as ExerciseEnum } from '@playbook-technologies/common-enums';
import {
  BuilderType,
  Circuit,
  LegacyExercise,
  Media,
  Running,
  RunningExerciseType,
  RunningTempo,
  Template,
  Video,
} from './workout.domain';
import {
  isRunningExercise,
  runningTemplateToExercise,
  toRunningTemplateData,
} from '../view-models/mappers';
import { omitObject } from '@client/new/helpers/general';

export const getPayloadFromValues = ({
  id,
  influencerId,
  values,
  isClass,
}: {
  influencerId: number;
  id?: number;
  values: LegacyWorkoutFormValues;
  isClass: boolean;
}): Partial<WorkoutTiny> => {
  const requiredEquipment = values.requiredEquipment
    ? values.requiredEquipment.split(',')
    : [];

  const exercises = values.exercises.map((ex: any) =>
    mapExerciseToPayload(ex, values, isClass),
  );

  return {
    id,
    influencerId,
    name: values.name,
    description: values.description || '',
    isProtected: !values.freeSample,
    exercises,
    duration: Number(values.duration),
    messagePush: values.messagePush,
    programId: values.programId,
    requiredEquipment,
    media: values?.introVideo || null,
    mediaCover: values?.thumbnail || null,
  };
};

export const workoutFormData = (
  data: WorkoutTiny,
): LegacyWorkoutFormValues => ({
  name: data.name || '',
  description: data.description || '',
  tags: data.tags || [],
  duration: data.duration || 0,
  id: (data as any).id,
  messagePush: data.messagePush || undefined,
  introVideo: (data.media as Media) || undefined,
  thumbnail: (data.mediaCover as Media) || undefined,
  programId: data.programId || undefined,
  freeSample: !data.isProtected,
  requiredEquipment: data.requiredEquipment
    ? data.requiredEquipment.join(', ')
    : undefined,
  exercises: mapExercises((data?.exercises as WorkoutExercise[]) || []),
  mediaExercise: (data.mediaExercise as Media) || undefined,
  publishedAt: data?.publishedAt || undefined,
  timeZone: data?.timeZone || undefined,
});

const mapExercises = (exercises?: any) => {
  return exercises?.map((e: any) => {
    const isRunning = isRunningExercise(e);
    if (!isRunning) return { ...e, uuid: uuid4() };

    return toRunningTemplateData(e);
  });
};

const mapExerciseToPayload = (exercise: any, values: any, isClass: boolean) => {
  switch (exercise.type) {
    case ExerciseEnum.Type.Single.value:
      return {
        ...omit(exercise, 'id', 'duration', 'uuid', '__typename'),
        sets: exercise.sets.map(({ reps }: { reps: [string, string] }) => ({
          reps: [Number(reps[0]), reps[1]],
        })),
        media: exercise.media && omit(exercise.media, '__typename'),
      };
    case 11:
    case 10:
      return { ...omitObject(exercise, ['uuid', '__typename']) };
    case ExerciseEnum.Type.Multiple.value:
      return {
        type: exercise.type,
        ...omit(exercise, ['uuid', '__typename']),
        duration: exercise.duration
          ? [Number(exercise.duration[0]), exercise.duration[1]]
          : null,
        exercises: exercise.exercises.map(mapExerciseToPayload),
        media: exercise.media && omit(exercise.media, '__typename'),
      };
    case ExerciseEnum.Type.YouTube.value:
      return {
        ...omit(exercise, ['uuid', '__typename']),
        duration: exercise?.media?.duration
          ? [Number(exercise.media.duration), 'sec']
          : null,
        media:
          exercise.media &&
          omit(exercise.media, [
            '__typename',
            'creatorId',
            'aspectRatio',
            'createdAt',
            'description',
            'title',
            'type',
            'deletedAt',
          ]),
        name: isClass ? values.name : exercise.name,
        description: isClass ? values.description : exercise.description,
      };
    case 100:
      return runningTemplateToExercise(exercise);
  }
};

export const exerciseTypeToString = (type: LegacyExercise['type']): string => {
  switch (type) {
    case ExerciseEnum.Type.Multiple.value:
      return 'Circuit';
    case ExerciseEnum.Type.YouTube.value:
      return 'Long Video';
    case 100:
      return 'Running';
    case ExerciseEnum.Type.Single.value:
    default:
      return 'Exercise';
  }
};

export const emptyExercise: LegacyExercise = {
  uuid: uuid4(),
  name: '',
  id: '',
  type: ExerciseEnum.Type.Single.value,
  description: '',
  rest: undefined,
  sets: [
    {
      reps: [0, 'reps'],
    },
  ],
  media: undefined,
};

export const emptyCircuit: Circuit = {
  uuid: uuid4(),
  id: '',
  name: '',
  rounds: 1,
  longRest: 60,
  type: ExerciseEnum.Type.Multiple.value as 3,
  description: '',
  rest: undefined,
  duration: [0, 'rounds'],
  exercises: [],
};

export const emptyTabata = { ...emptyCircuit, type: 10 };
export const emptyHIIT = { ...emptyCircuit, type: 11 };

export const emptyVideo: Video = {
  uuid: uuid4(),
  name: '',
  type: ExerciseEnum.Type.YouTube.value,
  description: '',
  media: undefined,
};

export const emptyOutdoorSet = {
  uuid: uuid4(),
  time: 1,
  distance: 1,
  tempo: RunningTempo.Walk,
  audio: undefined,
  uploading: false,
};

export const emptyRunningOutdoor: Running<RunningExerciseType.Outdoor> = {
  uuid: uuid4(),
  name: 'Running',
  measurementMode: 'time-based',
  type: 100,
  media: undefined,
  sets: [emptyOutdoorSet],
  variant: RunningExerciseType.Outdoor,
  uploading: false,
};

export const emptyTreadmillSet = {
  uuid: uuid4(),
  time: 0,
  speed: 0,
  incline: 0,
  audio: undefined,
  uploading: false,
};
export const emptyRunningTreadmill: Running<RunningExerciseType.Treadmill> = {
  uuid: uuid4(),
  name: 'Running',
  type: 100,
  media: undefined,
  measurementMode: 'time-based',
  sets: [emptyTreadmillSet],
  variant: RunningExerciseType.Treadmill,
  uploading: false,
};

export const initWorkout = (
  builderType: BuilderType,
  locationProgramId?: number,
) => ({
  name: '',
  description: '',
  tags: [],
  duration: undefined,
  messagePush: '',
  introVideo: undefined,
  thumbnail: undefined,
  programId: locationProgramId,
  freeSample: false,
  requiredEquipment: '',
  mediaExercise: undefined,
  exercises: builderType === 'class' ? [emptyVideo] : [],
});

export const initTemplates: Record<Template, object> = {
  exercise: emptyExercise,
  circuit: emptyCircuit,
  video: emptyVideo,
  running: emptyRunningOutdoor,
  HIIT: emptyHIIT,
  Tabata: emptyTabata,
};
